import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { App } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { ComponentSchemaService } from '~/app/services/knowledgeBase';
import { useAssetCustomFieldManagement } from '~/app/hooks/asset-custom-field/useAssetCustomFieldManagement';
import { getErrorMessage } from '~/app/utils/getErrorMessage';
import { AssetCustomFieldsPageView } from './AssetCustomFieldsPageView';
import type { AssetTypeOption, LookupRow } from './AssetCustomFieldsPage.types';

export function AssetCustomFieldsPageContainer() {
  return (
    <App>
      <AssetCustomFieldsPageContent />
    </App>
  );
}

function AssetCustomFieldsPageContent() {
  const { t } = useTranslation('assets');
  const { user, isLoading: isAuthLoading } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const [preferredAssetTypeId, setPreferredAssetTypeId] = useState('');

  const {
    data: assetTypeOptions = [],
    isLoading: isLoadingAssetTypes,
    error: assetTypesError,
  } = useQuery({
    queryKey: ['asset-types', 'custom-fields'],
    queryFn: async (): Promise<AssetTypeOption[]> => {
      const rows = await ComponentSchemaService.getLookup('Asset Type');

      return rows.map((row) => {
        const lookupRow = row as LookupRow;
        const name = String(lookupRow.name ?? lookupRow.code ?? lookupRow._key);
        const code = String(lookupRow.code ?? '');

        return {
          value: lookupRow._key,
          label: code ? `${code} · ${name}` : name,
          name,
          code,
        };
      });
    },
    enabled: isSuperAdmin && !isAuthLoading,
  });

  const selectedAssetTypeId = useMemo(() => {
    if (!isSuperAdmin) {
      return '';
    }

    const hasPreferredAssetType = assetTypeOptions.some(
      (option) => option.value === preferredAssetTypeId,
    );

    if (hasPreferredAssetType) {
      return preferredAssetTypeId;
    }

    return assetTypeOptions[0]?.value ?? '';
  }, [assetTypeOptions, preferredAssetTypeId, isSuperAdmin]);

  const selectedAssetType = useMemo(
    () =>
      assetTypeOptions.find((option) => option.value === selectedAssetTypeId) ??
      null,
    [assetTypeOptions, selectedAssetTypeId],
  );

  const {
    fields,
    isLoadingFields,
    fieldsError,
    fieldForm,
    fieldModalOpen,
    editingField,
    fieldTypeOptions,
    unitOptions,
    isSubmitting,
    openCreateField,
    openEditField,
    closeFieldModal,
    confirmDeleteField,
    submitFieldForm,
  } = useAssetCustomFieldManagement(selectedAssetTypeId);

  if (!isAuthLoading && !isSuperAdmin) {
    return <Navigate to="/home" replace />;
  }

  return (
    <AssetCustomFieldsPageView
      assetTypeOptions={assetTypeOptions}
      assetTypesErrorMessage={getErrorMessage(assetTypesError, t, null)}
      closeFieldModal={closeFieldModal}
      confirmDeleteField={confirmDeleteField}
      editingField={editingField}
      fieldForm={fieldForm}
      fieldModalOpen={fieldModalOpen}
      fieldTypeOptions={fieldTypeOptions}
      fields={fields}
      fieldsError={fieldsError}
      isLoadingAssetTypes={isLoadingAssetTypes || isAuthLoading}
      isLoadingFields={isLoadingFields}
      isSubmitting={isSubmitting}
      onAssetTypeChange={setPreferredAssetTypeId}
      onCreateField={openCreateField}
      onEditField={openEditField}
      selectedAssetTypeId={selectedAssetTypeId}
      selectedAssetTypeLabel={selectedAssetType?.label ?? null}
      submitFieldForm={submitFieldForm}
      unitOptions={unitOptions}
    />
  );
}
